import React, { useEffect, useState } from "react";
import { Loader2, ArrowRight, Activity, ShieldAlert, Cpu, Leaf, Truck, ShoppingCart, Settings, Award, Code, Database, Server } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import StartupCard from "../../components/discover/StartupCard";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const INDUSTRY_METADATA = {
  "Fintech": { icon: Database, color: "from-blue-500 to-indigo-600", desc: "Digital payments, lending, neobanking, and investment platforms." },
  "E-commerce": { icon: ShoppingCart, color: "from-pink-500 to-rose-600", desc: "Online retail marketplaces, direct-to-consumer (D2C) brands, and delivery." },
  "Edtech": { icon: Award, color: "from-amber-400 to-orange-500", desc: "Online education portals, tutoring, skill certification, and K-12 learning." },
  "SaaS": { icon: Code, color: "from-indigo-500 to-purple-600", desc: "Software as a Service products powering modern businesses globally." },
  "Logistics": { icon: Truck, color: "from-emerald-400 to-teal-500", desc: "Supply chain, automated delivery, freight management, and logistics." },
  "HealthTech": { icon: Activity, color: "from-cyan-500 to-teal-600", desc: "Telemedicine, health diagnostics, electronic records, and pharmacy delivery." },
  "FoodTech & Consumer": { icon: ShoppingCart, color: "from-red-500 to-orange-500", desc: "Quick-commerce delivery services, restaurant automation, and lifestyle brands." },
  "Mobility": { icon: Truck, color: "from-indigo-400 to-blue-500", desc: "Electric vehicles (EV), charging networks, mapping, and ride-hailing services." },
  "AI Startups": { icon: Cpu, color: "from-purple-600 to-pink-600", desc: "Generative AI models, speech recognition, computer vision, and machine learning platforms." },
  "Enterprise SaaS": { icon: Server, color: "from-sky-500 to-indigo-600", desc: "Cloud computing infrastructure, HR management, and enterprise scale-up solutions." },
  "Cyber Security": { icon: ShieldAlert, color: "from-red-600 to-rose-700", desc: "End-to-end security automation, malware prevention, and corporate threat scanning." },
  "Developer Tools": { icon: Code, color: "from-gray-700 to-slate-900", desc: "In-browser sandboxes, development environments, and collaborative coding IDEs." },
  "Agritech": { icon: Leaf, color: "from-green-500 to-emerald-600", desc: "Farm-to-fork distribution supply chain, crop analytics, and smart agriculture." },
  "Climate Tech": { icon: Leaf, color: "from-teal-500 to-green-600", desc: "Sustainable power grids, carbon calculators, and green battery technology." },
  "Biotech": { icon: Activity, color: "from-cyan-600 to-blue-700", desc: "Medical research, genetic mapping, bio-analytics, and advanced therapeutics." },
  "Deep Tech": { icon: Cpu, color: "from-indigo-700 to-violet-900", desc: "Hard science technology including space-tech, robotics, and advanced materials." }
};

export default function ExploreIndustriesPage() {
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [startupsLoading, setStartupsLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/discover/industries").then((r) => r.data.items || []),
      api.get("/discover/startups?limit=100").then((r) => r.data.items || [])
    ])
      .then(([indData, startupData]) => {
        if (alive) {
          // Calculate counts
          const list = indData.map((ind) => {
            const matches = startupData.filter(
              (s) =>
                s.industry === ind.name ||
                (ind.name === "AI Startups" && s.industry === "AI/ML") ||
                (ind.name === "HealthTech" && s.industry === "Healthcare")
            );
            return {
              ...ind,
              count: matches.length,
            };
          });
          setIndustries(list);
          setStartups(startupData);
        }
      })
      .catch((e) => console.error("Error loading industries:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const selectIndustryHandler = (indName) => {
    setStartupsLoading(true);
    setSelectedIndustry((prev) => (prev === indName ? null : indName));
    setTimeout(() => setStartupsLoading(false), 200);
  };

  const getActiveStartups = () => {
    if (!selectedIndustry) return [];
    return startups.filter(
      (s) =>
        s.industry === selectedIndustry ||
        (selectedIndustry === "AI Startups" && s.industry === "AI/ML") ||
        (selectedIndustry === "HealthTech" && s.industry === "Healthcare")
    );
  };

  const activeStartups = getActiveStartups();

  return (
    <DiscoverLayout
      title="Explore Industries"
      subtitle="Browse and analyze startups organized by sector, tech domains, and industry models. Select any industry to view active projects."
    >
      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
        </div>
      ) : (
        <div className="space-y-12">
          {/* Industry Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind) => {
              const meta = INDUSTRY_METADATA[ind.name] || { icon: Settings, color: "from-indigo-500 to-pink-500", desc: "Explore startups in this category." };
              const Icon = meta.icon;
              const isSelected = selectedIndustry === ind.name;

              return (
                <motion.div
                  key={ind.slug}
                  whileHover={{ y: -4 }}
                  onClick={() => selectIndustryHandler(ind.name)}
                  className={`cursor-pointer rounded-[24px] border p-6 flex flex-col justify-between shadow-sm transition-all duration-300 ${
                    isSelected
                      ? "bg-white border-[#7C3AED] ring-2 ring-[#7C3AED]/10"
                      : "bg-white border-gray-150 hover:border-[#7C3AED]/30 hover:shadow-md"
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.color} text-white flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-bold px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100/50">
                        {ind.count} {ind.count === 1 ? "Startup" : "Startups"}
                      </span>
                    </div>

                    <h3 className="font-display font-bold text-gray-900 text-base group-hover:text-[#7C3AED] transition">
                      {ind.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                      {meta.desc}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/discover/startups?industry=${ind.name}`);
                      }}
                      className="text-xs font-semibold text-gray-400 hover:text-[#7C3AED] transition flex items-center gap-1"
                    >
                      View All Startups <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-[10px] font-bold text-[#7C3AED]">
                      {isSelected ? "Collapse" : "Expand"}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Active Startups Inline Showcase */}
          <AnimatePresence mode="wait">
            {selectedIndustry && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                className="bg-white rounded-[24px] border border-gray-150 p-8 shadow-sm space-y-6"
              >
                <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-950 font-display">
                      {selectedIndustry} Startups
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                      Showcasing {activeStartups.length} startups matches for this category.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/discover/startups?industry=${selectedIndustry}`)}
                    className="px-4.5 py-2 text-xs font-bold text-white bg-[#7C3AED] rounded-xl hover:bg-[#6b32cc] transition shadow-sm"
                  >
                    Manage with Filters
                  </button>
                </div>

                {startupsLoading ? (
                  <div className="py-12 flex justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#7C3AED]" />
                  </div>
                ) : activeStartups.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">No matching startups in the current batch.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeStartups.map((c) => (
                      <StartupCard key={c.slug} c={c} />
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </DiscoverLayout>
  );
}
